import React, {FC, useEffect} from 'react';
import {connect} from 'react-redux';

import {ResolveDispatchProps} from '../../../../../../../../src/helpers/reduxHelpers';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../../../src/services/tracking';
import {getRunningImportContactsCsvJobs} from '../../../../../../../../src/store/data/jobs/jobsSelectors';
import {getUserNamespace} from '../../../../../../../../src/store/data/teammates/teammatesSelectors';
import {
  getContactsManagerSidePanelState,
  getContactsManagerTableState,
  makeGetContactsManagerPath,
  makeGetIsInMultiSelectMode,
  makeGetRouteCardId,
  makeGetSelectedCardIds,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {tableStateIsContactsTableState} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../../../../../src/store/stateless/navigationActions';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../srcCommon/helpers/reselectHelpers';
import {useToggle} from '../../../../../../../shared/helpers/src/react/hooks/hookHelpers';
import {AutoContactsPageContainer} from './autoContactsPageContainer';
import {PrivateContactsPageContainer} from './privateContactsPageContainer';
import {SharedContactsPageContainer} from './sharedContactsPageContainer';

/*
 * Props.
 */

interface ContactsPageContainerPublicProps {
  panelId: ContactsManagerPanelId;
  contactsRouteType: ContactsManagerParametersTypesEnum;
}

type ContactsPageContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type ContactsPageContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type ContactsPageContainerComponentProps = ContactsPageContainerPublicProps &
  ContactsPageContainerStateProps &
  ContactsPageContainerDispatchProps;

/*
 * Selectors.
 */

function makeGetContactsTableState() {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (!tableStateIsContactsTableState(tableState)) {
      return undefined;
    }
    return tableState;
  });
}

const getHasRunningImportContactsCSVJobs = createSelector(
  [getRunningImportContactsCsvJobs],
  (contactsJobs) => contactsJobs.length > 0,
);

/*
 * Component.
 */

const ContactsPageContainerComponent: FC<ContactsPageContainerComponentProps> = (props) => {
  const {contactsRouteType, hasRunningImportContactsCSVJobs} = props;
  const [isShowingImportCSVDialog, onToggleImportCSVClick] = useToggle(false);

  useEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CONTACTS_SECTION_VIEW, {
      section_type: contactsRouteType,
    });
  }, [contactsRouteType]);

  switch (contactsRouteType) {
    case ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS:
      return (
        <PrivateContactsPageContainer
          hasRunningImportContactsCSVJobs={hasRunningImportContactsCSVJobs}
          isShowingImportCSVDialog={isShowingImportCSVDialog}
          onToggleImportCSVClick={onToggleImportCSVClick}
        />
      );
    case ContactsManagerParametersTypesEnum.AUTO_CONTACTS:
      return (
        <AutoContactsPageContainer
          hasRunningImportContactsCSVJobs={hasRunningImportContactsCSVJobs}
          isShowingImportCSVDialog={isShowingImportCSVDialog}
          onToggleImportCSVClick={onToggleImportCSVClick}
        />
      );
    case ContactsManagerParametersTypesEnum.SHARED_CONTACTS:
    default:
      return (
        <SharedContactsPageContainer
          hasRunningImportContactsCSVJobs={hasRunningImportContactsCSVJobs}
          isShowingImportCSVDialog={isShowingImportCSVDialog}
          onToggleImportCSVClick={onToggleImportCSVClick}
        />
      );
  }
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactsPageContainerPublicProps>()(() => ({
  routeCardId: makeGetRouteCardId(),
  isInMultiSelectMode: makeGetIsInMultiSelectMode(),
  selectedTableCardIds: makeGetSelectedCardIds(),
  tableState: makeGetContactsTableState(),
  sidePanelState: getContactsManagerSidePanelState,
  userNamespace: getUserNamespace,
  url: makeGetContactsManagerPath(),
  hasRunningImportContactsCSVJobs: getHasRunningImportContactsCSVJobs,
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  navigateToLink: navigateToLinkAction,
}));

export const ContactsPageContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactsPageContainerComponent);
